'use client';

import { useEffect, useState, type MouseEvent } from 'react';
import { IconButton, Tooltip, type SxProps, type Theme } from '@mui/material';
import { IosShareRounded } from '@mui/icons-material';
import { useSnackbarContext } from '@/components/SnackbarProvider';
import { canShareFiles, shareFile } from '@/lib/app/shareFile';

export function useCanShareFiles() {
  const [supported, setSupported] = useState(false);
  useEffect(() => setSupported(canShareFiles()), []);
  return supported;
}

/** Share icon for a label, invoice or photo. Renders only on mobile browsers with a share sheet. */
export default function ShareFileButton({ url, name, fontSize = 15, sx }: { url: string; name: string; fontSize?: number; sx?: SxProps<Theme> }) {
  const supported = useCanShareFiles();
  const { showError } = useSnackbarContext();
  const [busy, setBusy] = useState(false);
  if (!supported) return null;

  const share = (event: MouseEvent) => {
    event.stopPropagation();
    setBusy(true);
    void shareFile(url, name)
      .catch(() => showError('Failed to share file'))
      .finally(() => setBusy(false));
  };

  return (
    <Tooltip title="Share">
      <IconButton size="small" disabled={busy} onClick={share} aria-label={`Share ${name}`} sx={sx}>
        <IosShareRounded sx={{ fontSize }} />
      </IconButton>
    </Tooltip>
  );
}
